import { Component, input, ChangeDetectionStrategy, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BizEntityTypeCategory, IBizEntity, IRights } from '@optima-stride-ai/core';
import { PdfViewerComponent } from "../../pdf-viewer/pdf-viewer.component";
import { ImageViewerComponent } from "../../image-viewer/image-viewer.component";
import { VideoViewerComponent } from "../../video-viewer/video-viewer.component";
import { MarkdownEditorComponent } from "../markdown-editor/markdown-editor.component";
import { CollaboraViewerComponent } from "../../collabora-viewer/collabora-viewer.component";
import { WhiteboardViewerComponent } from "../../whiteboard-viewer/whiteboard-viewer.component";
import { FileOperationsService } from '../services/file-operations.service';
import { ToastService } from '@optima-stride-ai/optima-components';

/**
 * Container component responsible for routing to the appropriate file viewer
 * based on file category/type. This separates viewer logic from the main component.
 */
@Component({
  selector: 'app-file-viewer-container',
  imports: [
    CommonModule,
    PdfViewerComponent,
    ImageViewerComponent,
    VideoViewerComponent,
    MarkdownEditorComponent,
    CollaboraViewerComponent,
    WhiteboardViewerComponent
  ],
  templateUrl: './file-viewer-container.component.html',
  styles: [`
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileViewerContainerComponent {
  @ViewChild(CollaboraViewerComponent) private collaboraViewer?: CollaboraViewerComponent;

  private readonly fileOperations = inject(FileOperationsService);
  private readonly toastService = inject(ToastService);
  /** Only mount an editor once its backing file has been persisted. */
  protected readonly readyEntityId = signal('');
  /** Prepared metadata is forwarded only when the iframe host supplies an entity. */
  protected readonly preparedEntity = signal<IBizEntity | undefined>(undefined);

  // Input signals for reactive data binding
  isViewerActive = input.required<boolean>();
  category = input.required<BizEntityTypeCategory | number>();
  bizEntityId = input.required<string>();
  mode = input.required<IRights>();
  onBack = input<(() => void) | undefined>(undefined);
  /** Optional server-loaded iframe entity; regular-app consumers keep their existing flow. */
  entity = input<IBizEntity>();

  // Expose BizEntityTypeCategory for template use
  readonly bizEntityTypeCategory = BizEntityTypeCategory;

  /** Gates regular and iframe viewers, ignoring completions after navigation or destruction. */
  private readonly initializeFile = effect(onCleanup => {
    const entityId = this.bizEntityId();
    const active = this.isViewerActive();
    const category = this.category();
    const loadedEntity = this.entity();
    this.readyEntityId.set('');
    this.preparedEntity.set(undefined);
    if (!active || !entityId) return;
    if (!this.fileOperations.canCreateBlankFile(category)) {
      this.readyEntityId.set(entityId);
      return;
    }

    let cancelled = false;
    onCleanup(() => { cancelled = true; });
    const initialization = loadedEntity && String(loadedEntity.id) === String(entityId)
      ? this.fileOperations.ensureEntityFile(entityId, loadedEntity)
      : this.fileOperations.ensureEntityFile(entityId);
    void initialization.then(prepared => {
      if (!cancelled) {
        if (loadedEntity && String(loadedEntity.id) === String(entityId)) {
          this.preparedEntity.set(prepared);
        }
        this.readyEntityId.set(entityId);
      }
    }).catch(() => {
      if (!cancelled) {
        this.toastService.toast('Unable to prepare this file. Please reopen it to try again.');
      }
    });
  });

  prepareForCollaboraClose(): Promise<void> {
    return this.collaboraViewer?.prepareForClose() ?? Promise.resolve();
  }
}
